import { toast } from "@/hooks/use-toast";
import { paymentsService, RelacaoPagamentos } from "@/services/end-points/payments";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface IError {
  data?: {
    message?: string;
  };
  message?: string;
}

export const useUploadNF = () => {
  const mensagemToast = (type: "success" | "destructive", err?: IError) => ({
    title: type === "success" ? "Nota fiscal enviada" : "Erro ao tentar enviar nota fiscal",
    description: type === "success" ? "Nota fiscal enviada com sucesso" : err?.data?.message ?? "Não foi possível enviar a nota fiscal",
    variant: type,
  })

  return useMutation({
    mutationFn: ({ file, contractId }: { file: FormData; contractId: number }) =>
      paymentsService.uploadNF(file, contractId),
    onSuccess: () => {
      toast(mensagemToast("success"));
    },
    onError: (err: IError) => {
      toast(mensagemToast("destructive", err));
    }
  });
};

export const useSave = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  const mensagemToast = (type: "success" | "destructive", err?: IError) => ({
    title: type === "success" ? "Item criado" : "Erro ao criar item",
    description: type === "success" ? "Item de orçamento criado com sucesso" : err?.data?.message ?? "Não foi possível criar o item de orçamento",
    variant: type,
  })

  return useMutation({
    mutationFn: (data: RelacaoPagamentos) => paymentsService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments-list'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      queryClient.invalidateQueries({ queryKey: ['budget-table'] });

      toast(mensagemToast("success"));
      if (onSuccess) onSuccess();
    },
    onError: (err: IError) => {
      toast(mensagemToast("destructive", err));
    }
  });
};

export const useUpdate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  const mensagemToast = (type: "success" | "destructive", err?: IError) => ({
    title: type === "success" ? "Pagamento atualizado" : "Erro ao atualizar pagamento",
    description: type === "success" ? "Pagamento atualizado com sucesso" : err?.data?.message ?? "Não foi possível atualizar o pagamento",
    variant: type,
  })

  return useMutation({
    mutationFn: ({id, data}: {id: number, data: Partial<RelacaoPagamentos>}) => paymentsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments-list'] });

      toast(mensagemToast("success"));
      if (onSuccess) onSuccess();
    },
    onError: (err: IError) => {
      toast(mensagemToast("destructive", err));
    }
  });
};

export const useFindAll = (movieId: number) => {
  return useQuery({
    queryKey: ['payments-list', movieId],
    queryFn: () => paymentsService.findAllByMovieId(movieId),
    enabled: !!movieId,
  });
}